import { supabase } from "./supabase";

// Legacy ids keep a rolling App/agent deployment compatible. The promoted
// agent returns only `host` after the cutover.
export type HostRoot = { id: "host" | "app" | "infra" | "operations"; name: string };
export type HostEntry = { name: string; kind: "directory" | "file" | "link" | "blocked"; size: number | null; modifiedAt: string };
export type HostDirectory = { rootId: HostRoot["id"]; path: string; entries: HostEntry[]; truncated: boolean };
export type HostFile = { rootId: HostRoot["id"]; path: string; content: string; etag: string };
export type HostTerminalMode = "shell" | "codex";
export type ServiceState = "operational" | "unavailable";
export type OsExternalBackupStatus = {
  state: "operational" | "attention" | "not_configured" | "unavailable";
  scope: "base-sul-os"; completedAt: string | null; ageSeconds: number | null; sizeBytes: number | null;
  offsite: boolean; encrypted: boolean; readbackVerified: boolean; restoreVerified: boolean;
};
export type SystemStatus = {
  checkedAt: string;
  host: {
    cpuCount: number;
    loadAverage: { one: number; five: number; fifteen: number };
    memory: { totalBytes: number; availableBytes: number };
    disk: { totalBytes: number; availableBytes: number };
    uptimeSeconds: number;
  };
  services: { application: ServiceState; auth: ServiceState; database: ServiceState; storage: ServiceState };
  backup: {
    state: "operational" | "attention" | "not_configured";
    mode: "local";
    completedAt: string | null;
    ageSeconds: number | null;
    retentionDays: number | null;
    sizeBytes: number | null;
    offsite: false;
  };
  osExternalBackup?: OsExternalBackupStatus;
  guard: {
    state: "healthy" | "warning" | "critical" | "unknown";
    observedAt: string | null;
    browserPages?: number | null;
    browserMemoryBytes?: number | null;
  };
};
export type HostProject = { name: string; path: string; scope: string; git: boolean };
export type BrowserRuntime = { id: HostBrowserId; pages: number | null; memoryBytes: number; connected: boolean; reserved: boolean };
export type TemporaryWorkload = { name: string; unit: string; state: string; subState: string };
export type ControlCenter = {
  checkedAt: string;
  guard: null | {
    generatedAt?: unknown;
    projectBrowserPool?: unknown;
    health?: string;
    cpu?: { count?: number; load1?: number; load5?: number; load15?: number; pressureAvg10?: number };
    memory?: { availableKiB?: number; pressureSomeAvg10?: number; pressureFullAvg10?: number };
    swap?: { usedKiB?: number };
    browserPool?: { memoryBytes?: number; pages?: number; pressureSamples?: number; intervention?: { action?: string } };
    codexAuxiliarySessions?: { running?: number; stale?: number; terminatedThisRun?: number };
    managedTemporaryUnits?: number;
    actions?: unknown[];
  };
  codexUpdater: { state: string; checkedAt: string | null; version: string | null; pendingRestart: boolean; message: string };
  sessions: { codex: number; terminal: number };
  browsers: BrowserRuntime[];
  temporaryWorkloads: TemporaryWorkload[];
  projects: HostProject[];
};
export type ControlAction = "relieve_browser_memory" | "check_codex_update" | "disconnect_codex" | "disconnect_browser" | "stop_temporary_workload";
export const maxHostUploadBytes = 50 * 1024 * 1024;
export const hostWorkspaceChangedEvent = "base-sul-os:host-workspace-changed";

export class HostWorkspaceError extends Error {
  constructor(message: string, readonly code = "workspace_unavailable") {
    super(message);
    this.name = "HostWorkspaceError";
  }
}

export async function listHostRoots(): Promise<HostRoot[]> {
  const payload = await request("/roots");
  if (!payload || typeof payload !== "object" || !Array.isArray((payload as { roots?: unknown }).roots)) throw invalidResponse();
  return (payload as { roots: unknown[] }).roots.map(parseRoot);
}

export async function getSystemStatus(): Promise<SystemStatus> {
  const payload = await request("/system-status");
  if (!isSystemStatus(payload)) throw invalidResponse();
  return payload;
}

export async function getControlCenter(): Promise<ControlCenter> {
  const payload = await request("/control-center");
  if (!isControlCenter(payload)) throw invalidResponse();
  return payload;
}

export async function runControlAction(action: ControlAction, target?: string): Promise<void> {
  await request("/control-action", { method: "POST", body: JSON.stringify({ action, target }) });
}

export async function listHostProjects(): Promise<HostProject[]> {
  return (await getControlCenter()).projects;
}

export async function listHostDirectory(rootId: HostRoot["id"], path = ""): Promise<HostDirectory> {
  const payload = await request(`/files?root=${encodeURIComponent(rootId)}&path=${encodeURIComponent(path)}`);
  if (!payload || typeof payload !== "object" || !Array.isArray((payload as { entries?: unknown }).entries)) throw invalidResponse();
  const row = payload as Record<string, unknown>;
  if (row.rootId !== rootId || typeof row.path !== "string" || typeof row.truncated !== "boolean") throw invalidResponse();
  return { rootId, path: row.path, truncated: row.truncated, entries: (row.entries as unknown[]).map(parseEntry) };
}

export async function getHostDirectorySize(rootId: HostRoot["id"], path: string): Promise<number | null> {
  const payload = await request(`/directory-size?root=${encodeURIComponent(rootId)}&path=${encodeURIComponent(path)}`);
  const row = payload as Record<string, unknown> | null;
  if (!row || row.rootId !== rootId || row.path !== path || !(row.size === null || (typeof row.size === "number" && Number.isSafeInteger(row.size) && row.size >= 0))) throw invalidResponse();
  return row.size as number | null;
}

export async function readHostFile(rootId: HostRoot["id"], path: string): Promise<HostFile> {
  const payload = await request(`/file?root=${encodeURIComponent(rootId)}&path=${encodeURIComponent(path)}`);
  const row = payload as Record<string, unknown> | null;
  if (!row || row.rootId !== rootId || row.path !== path || typeof row.content !== "string" || typeof row.etag !== "string") throw invalidResponse();
  return { rootId, path, content: row.content, etag: row.etag };
}

export async function writeHostFile(file: HostFile, content: string): Promise<string> {
  const payload = await request("/file", { method: "PUT", body: JSON.stringify({ rootId: file.rootId, path: file.path, content, etag: file.etag }) });
  if (!payload || typeof payload !== "object" || typeof (payload as { etag?: unknown }).etag !== "string") throw invalidResponse();
  return (payload as { etag: string }).etag;
}

export async function createHostDirectory(rootId: HostRoot["id"], path: string): Promise<void> {
  await request("/directory", { method: "POST", body: JSON.stringify({ rootId, path }) });
}

export async function uploadHostFile(rootId: HostRoot["id"], path: string, file: File): Promise<void> {
  if (file.size > maxHostUploadBytes) throw new HostWorkspaceError("Cada arquivo pode ter no máximo 50 MB.", "upload_too_large");
  await request(`/upload?root=${encodeURIComponent(rootId)}&path=${encodeURIComponent(path)}`, {
    method: "POST",
    body: file,
    headers: { "Content-Type": "application/octet-stream", "X-Upload-Size": String(file.size) },
  });
}

export async function renameHostEntry(rootId: HostRoot["id"], sourcePath: string, targetPath: string): Promise<void> {
  await request("/rename", { method: "POST", body: JSON.stringify({ rootId, sourcePath, targetPath }) });
}

export async function copyHostEntry(rootId: HostRoot["id"], sourcePath: string, targetPath: string): Promise<void> {
  await request("/copy", { method: "POST", body: JSON.stringify({ rootId, sourcePath, targetPath }) });
}

export async function moveHostEntry(rootId: HostRoot["id"], sourcePath: string, targetPath: string): Promise<void> {
  await request("/move", { method: "POST", body: JSON.stringify({ rootId, sourcePath, targetPath }) });
}

export async function deleteHostEntry(rootId: HostRoot["id"], path: string): Promise<void> {
  await request("/entry", { method: "DELETE", body: JSON.stringify({ rootId, path }) });
}

export async function createHostDownload(rootId: HostRoot["id"], path: string): Promise<{ url: string; filename: string; expiresAt: number }> {
  const payload = await request("/download-ticket", { method: "POST", body: JSON.stringify({ rootId, path }) });
  const row = payload as Record<string, unknown> | null;
  if (!row || typeof row.filename !== "string" || typeof row.expiresIn !== "number" || !Number.isFinite(row.expiresIn) || row.expiresIn <= 0) throw invalidResponse();
  const url = workspaceBaseUrl();
  url.pathname = `${url.pathname}download`;
  return { url: url.toString(), filename: row.filename, expiresAt: Date.now() + row.expiresIn * 1_000 };
}

export function notifyHostWorkspaceChanged(): void {
  window.dispatchEvent(new Event(hostWorkspaceChangedEvent));
}

export async function createTerminalTicket(rootId: HostRoot["id"], mode: HostTerminalMode): Promise<{ ticket: string; websocketUrl: string }> {
  const payload = await request("/terminal-ticket", { method: "POST", body: JSON.stringify({ rootId, mode }) });
  if (!payload || typeof payload !== "object" || typeof (payload as { ticket?: unknown }).ticket !== "string") throw invalidResponse();
  const url = workspaceBaseUrl();
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.pathname = `${url.pathname}terminal`;
  return { ticket: (payload as { ticket: string }).ticket, websocketUrl: url.toString() };
}

export async function createCodexTicket(): Promise<{ ticket: string; websocketUrl: string }> {
  const payload = await request("/codex-ticket", { method: "POST", body: "{}" });
  if (!payload || typeof payload !== "object" || typeof (payload as { ticket?: unknown }).ticket !== "string") throw invalidResponse();
  const url = workspaceBaseUrl();
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.pathname = `${url.pathname}codex`;
  return { ticket: (payload as { ticket: string }).ticket, websocketUrl: url.toString() };
}

export type HostBrowserId = "principal" | "projeto-2" | "projeto-3" | `project:${string}`;
export type HostBrowser = { id: HostBrowserId; name: string; kind: "project" | "legacy"; active: boolean; mode: "available" | "manual" | "automation" };

export async function listHostBrowsers(): Promise<HostBrowser[]> {
  const payload = await request("/browsers") as { browsers?: unknown[] } | null;
  if (!Array.isArray(payload?.browsers)) throw invalidResponse();
  const seen = new Set<string>();
  return payload.browsers.map((value) => {
    const row = value as Partial<HostBrowser> | null;
    if (!row || !isHostBrowserId(row.id) || typeof row.name !== "string" || !row.name.trim()
      || row.name.length > 200 || typeof row.active !== "boolean" || !["available", "manual", "automation"].includes(row.mode ?? "")
      || (row.kind !== "project" && row.kind !== "legacy") || (row.kind === "project") !== row.id.startsWith("project:") || seen.has(row.id)) throw invalidResponse();
    seen.add(row.id);
    return row as HostBrowser;
  });
}

export function isHostBrowserId(value: unknown): value is HostBrowserId {
  return value === "principal" || value === "projeto-2" || value === "projeto-3"
    || (typeof value === "string" && /^project:[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(value));
}

export async function releaseBrowserTicket(browserId: HostBrowserId, ticket: string): Promise<void> {
  await request("/browser-release", { method: "POST", body: JSON.stringify({ browserId, ticket }), keepalive: true });
}

export async function createBrowserTicket(browserId: HostBrowserId = "principal"): Promise<{ ticket: string; websocketUrl: string }> {
  const payload = await request("/browser-ticket", { method: "POST", body: JSON.stringify({ browserId }) });
  if (!payload || typeof payload !== "object" || typeof (payload as { ticket?: unknown }).ticket !== "string") throw invalidResponse();
  const url = workspaceBaseUrl();
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.pathname = `${url.pathname}browser`;
  return { ticket: (payload as { ticket: string }).ticket, websocketUrl: url.toString() };
}

async function request(path: string, init: RequestInit = {}) {
  if (!supabase) throw new HostWorkspaceError("Backend não configurado.", "backend_not_configured");
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new HostWorkspaceError("Sua sessão expirou. Entre novamente.", "authentication_required");
  const response = await fetch(new URL(path.replace(/^\//, ""), workspaceBaseUrl()), {
    ...init,
    credentials: "include",
    headers: { Authorization: `Bearer ${token}`, ...(typeof init.body === "string" ? { "Content-Type": "application/json" } : {}), ...init.headers },
  }).catch(() => null);
  if (!response) throw new HostWorkspaceError("O workspace está temporariamente indisponível.");
  const payload = await response.json().catch(() => null) as { error?: unknown } | null;
  if (!response.ok) {
    const code = typeof payload?.error === "string" ? payload.error : "workspace_unavailable";
    throw new HostWorkspaceError(errorMessage(code), code);
  }
  return payload;
}

function workspaceBaseUrl() {
  const configured = import.meta.env.VITE_SUPABASE_URL?.trim();
  if (!configured) throw new HostWorkspaceError("Backend não configurado.", "backend_not_configured");
  return new URL("/workspace/v1/", configured);
}

function parseRoot(value: unknown): HostRoot {
  const row = value as Record<string, unknown> | null;
  if (!row || !isRootId(row.id) || typeof row.name !== "string") throw invalidResponse();
  return { id: row.id, name: row.name };
}

function parseEntry(value: unknown): HostEntry {
  const row = value as Record<string, unknown> | null;
  if (!row || typeof row.name !== "string" || !["directory", "file", "link", "blocked"].includes(String(row.kind)) || !(row.size === null || typeof row.size === "number") || typeof row.modifiedAt !== "string") throw invalidResponse();
  return row as HostEntry;
}

function isNonNegativeNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0;
}

function isSystemStatus(value: unknown): value is SystemStatus {
  const row = value as Partial<SystemStatus> | null;
  if (!row?.host || !row.services || !row.backup || !row.guard) return false;
  const host = row?.host;
  const load = host?.loadAverage;
  const memory = host?.memory;
  const disk = host?.disk;
  const services = row?.services;
  const backup = row?.backup;
  const guard = row?.guard;
  const serviceStates = [services?.application, services?.auth, services?.database, services?.storage];
  return Boolean(typeof row.checkedAt === "string" && Number.isFinite(Date.parse(row.checkedAt))
    && Number.isInteger(host?.cpuCount) && host.cpuCount > 0
    && [load?.one, load?.five, load?.fifteen, memory?.totalBytes, memory?.availableBytes, disk?.totalBytes, disk?.availableBytes, host?.uptimeSeconds].every(isNonNegativeNumber)
    && memory.availableBytes <= memory.totalBytes && disk.availableBytes <= disk.totalBytes
    && serviceStates.every((state) => state === "operational" || state === "unavailable")
    && ["operational", "attention", "not_configured"].includes(backup?.state)
    && backup?.mode === "local" && backup?.offsite === false
    && (backup.completedAt === null || (typeof backup.completedAt === "string" && Number.isFinite(Date.parse(backup.completedAt))))
    && (backup.ageSeconds === null || isNonNegativeNumber(backup.ageSeconds))
    && (backup.retentionDays === null || (Number.isInteger(backup.retentionDays) && backup.retentionDays > 0))
    && (backup.sizeBytes === null || isNonNegativeNumber(backup.sizeBytes))
    && (row.osExternalBackup === undefined || isOsExternalBackupStatus(row.osExternalBackup))
    && ["healthy", "warning", "critical", "unknown"].includes(guard?.state)
    && (guard.observedAt === null || (typeof guard.observedAt === "string" && Number.isFinite(Date.parse(guard.observedAt)))));
}

function isOsExternalBackupStatus(value: unknown): value is OsExternalBackupStatus {
  const row = value as Partial<OsExternalBackupStatus> | null;
  return Boolean(row && ["operational", "attention", "not_configured", "unavailable"].includes(String(row.state)) && row.scope === "base-sul-os"
    && (row.completedAt === null || (typeof row.completedAt === "string" && Number.isFinite(Date.parse(row.completedAt))))
    && (row.ageSeconds === null || isNonNegativeNumber(row.ageSeconds)) && (row.sizeBytes === null || isNonNegativeNumber(row.sizeBytes))
    && [row.offsite, row.encrypted, row.readbackVerified, row.restoreVerified].every((flag) => typeof flag === "boolean")
    && (!["operational", "attention"].includes(String(row.state)) || (row.offsite && row.encrypted && row.readbackVerified && row.restoreVerified && row.completedAt !== null && row.ageSeconds !== null && row.sizeBytes !== null)));
}

function isControlCenter(value: unknown): value is ControlCenter {
  const row = value as Partial<ControlCenter> | null;
  return Boolean(row && typeof row.checkedAt === "string"
    && row.sessions && Number.isInteger(row.sessions.codex) && Number.isInteger(row.sessions.terminal)
    && Array.isArray(row.browsers) && row.browsers.every((item) => item && isRootBrowserId(item.id)
      && (item.pages === null || Number.isInteger(item.pages)) && isNonNegativeNumber(item.memoryBytes)
      && typeof item.connected === "boolean" && typeof item.reserved === "boolean")
    && Array.isArray(row.temporaryWorkloads)
    && Array.isArray(row.projects) && row.projects.every((project) => project && typeof project.name === "string"
      && typeof project.path === "string" && project.path.startsWith("/home/nectar/projetos")
      && typeof project.scope === "string" && typeof project.git === "boolean")
    && row.codexUpdater && typeof row.codexUpdater.state === "string" && typeof row.codexUpdater.message === "string");
}

function isRootBrowserId(value: unknown): value is HostBrowserId {
  return value === "principal" || value === "projeto-2" || value === "projeto-3";
}

function isRootId(value: unknown): value is HostRoot["id"] {
  return value === "host" || value === "app" || value === "infra" || value === "operations";
}

function invalidResponse() {
  return new HostWorkspaceError("O workspace retornou uma resposta inválida.", "invalid_response");
}

function errorMessage(code: string) {
  const messages: Record<string, string> = {
    authentication_required: "Sua sessão expirou. Entre novamente.",
    workspace_access_denied: "Sua conta não está ativa no Base Sul OS.",
    entry_not_found: "O arquivo ou pasta não existe mais.",
    invalid_path: "O caminho informado é inválido.",
    symbolic_link_denied: "Links simbólicos não podem ser abertos no navegador.",
    file_too_large: "Este arquivo é grande demais para o editor do navegador.",
    file_changed: "O arquivo mudou desde que foi aberto. Recarregue antes de salvar.",
    target_exists: "Já existe um item com esse nome na pasta de destino.",
    invalid_destination: "Não é possível colocar uma pasta dentro dela mesma.",
    entry_type_denied: "Esse tipo de item não pode ser manipulado pelo Explorador.",
    invalid_upload_size: "O tamanho recebido não corresponde ao arquivo selecionado.",
    invalid_upload_type: "O formato deste envio não é aceito pela VPS.",
    upload_too_large: "Cada arquivo pode ter no máximo 50 MB.",
    upload_interrupted: "O envio foi interrompido. O arquivo incompleto foi descartado.",
    insufficient_storage: "A VPS está com pouco espaço livre. Libere espaço antes de enviar novos arquivos.",
    operation_denied: "A VPS recusou esta operação para o item selecionado.",
    terminal_already_active: "Você já possui um terminal aberto.",
    codex_already_active: "O Codex já está aberto em outra janela ou aba.",
    browser_already_active: "O Navegador já está aberto em outra janela ou aba.",
    browser_unavailable: "O Navegador da VPS está temporariamente indisponível.",
    project_browser_unavailable: "O gerenciador de navegadores dos projetos está indisponível. Nenhum perfil antigo foi aberto como alternativa.",
    project_browser_busy: "Aguardando a ação atual do chat ou outro operador terminar. O navegador não será interrompido.",
    project_browser_capacity: "Os navegadores de projetos estão ocupados. Feche um visualizador ou aguarde uma tarefa liberar o controle.",
    invalid_browser_id: "Este projeto não está cadastrado no gerenciador de navegadores.",
    invalid_control_action: "Esta ação operacional não é permitida.",
    invalid_workload: "O preview temporário informado é inválido.",
    workload_not_found: "O preview temporário não está mais disponível.",
    host_command_failed: "A VPS não conseguiu concluir a ação operacional.",
  };
  return messages[code] ?? "O workspace está temporariamente indisponível.";
}
